import React, { useState, useEffect } from "react";
import "./Integrations.css";
import IntegrationModal from "./IntegrationModal/IntegrationModal";
import { Database, GraduationCap, Plus } from "lucide-react";
import customFetch from "../../../helpers/axios";
import Button from "../../components/Buttons/Button/Button";
import SearchInput from "../../components/SearchInput/SearchInput";
import { useTranslation } from "react-i18next";
import useFormatDate from "../../../hooks/useFormatDate";
import useUser from "../../../hooks/useUser";

export default function Integrations() {
  const { t } = useTranslation('integrations');
  const formatDate = useFormatDate();
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [integrationToConfigure, setIntegrationToConfigure] = useState(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [configData, setConfigData] = useState(null);
  
  const [isDeletingIntegration, setIsDeletingIntegration] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useUser();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (!(user && user.access)) return;
    const fetchIntegrations = async () => {
      try {
        const response = await customFetch.get(
          "/api/integrations/organization_platform/"
        );
        const integrationsData = response.data.results || response.data;
        setIntegrations(
          Array.isArray(integrationsData) ? integrationsData : []
        );
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error("Error fetching organization_platform list:", err);
      }
    };
    fetchIntegrations();
  }, [user]);

  // Handler para abrir modal de configuración y cargar datos existentes
  const handleConfigureIntegration = async (integration) => {
    setIntegrationToConfigure(integration);
    setShowConfigureModal(true);
    setIsSavingConfig(false);
    try {
      // Buscar por ID específico para obtener datos completos
      const url = `/api/integrations/organization_platform/${integration.id}/`;
      const response = await customFetch.get(url);
      setConfigData(response.data);
    } catch (error) {
      console.error("Error fetching organization_platform details:", error);
      setConfigData(null);
    }
  };

  // Handler para guardar config
  const handleSaveConfigIntegration = async (configPayload, onError) => {
    if (!integrationToConfigure) return;
    setIsSavingConfig(true);
    try {
      // PATCH configuración de plataforma por PK
      // Enviar únicamente other_urls como objeto (JSONField)
      // Build PATCH payload: only configurable fields. Do NOT send organization/integration here.
      let normalizedBaseUrl = configPayload?.base_url ?? "";
      if (typeof normalizedBaseUrl === 'string') {
        const trimmed = normalizedBaseUrl.trim();
        if (trimmed && !/^https?:\/\//i.test(trimmed)) {
          normalizedBaseUrl = `https://${trimmed}`;
        } else {
          normalizedBaseUrl = trimmed;
        }
      }

      const payload = {
        base_url: normalizedBaseUrl,
        secret_key: configPayload?.secret_key,
        client_id: configPayload?.client_id,
        other_urls:
          typeof configPayload?.other_urls === "string"
            ? (configPayload.other_urls.trim() ? JSON.parse(configPayload.other_urls) : {})
            : (configPayload?.other_urls || {}),
      };
      if (configData && configData.id) {
        await customFetch.patch(
          `/api/integrations/organization_platform/${configData.id}/`,
          payload
        );
      }
      // Si llegamos aquí, la operación fue exitosa
      setShowConfigureModal(false);
      setIntegrationToConfigure(null);
      setConfigData(null);
      // Recargar la lista de integraciones
      const response = await customFetch.get(
        "/api/integrations/organization_platform/"
      );
      const integrationsData = response.data.results || response.data;
      setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
    } catch (error) {
      if (error.response && error.response.data) {
        // Llamar la función de error del modal
        if (typeof onError === "function") {
          onError(error.response.data);
        }
      } else {
        // Error genérico
        if (typeof onError === "function") {
          onError({ general: ["An error occurred. Please try again."] });
        }
      }
      // Rechazar la promesa para que el modal no se cierre
      throw error;
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Create handler for organization_platform
  const handleCreateOrganizationPlatform = async (formData) => {
    try {
      // Validate other_urls: allow empty -> {}, non-empty must be valid JSON
      let otherUrlsObj = {};
      if (typeof formData.other_urls === "string") {
        const str = formData.other_urls.trim();
        if (str) {
          try {
            otherUrlsObj = JSON.parse(str);
          } catch (e) {
            const err = new Error("Invalid JSON for other_urls");
            err.response = {
              data: {
                other_urls: [ t('modal.errors.invalidJson') ],
              },
            };
            throw err;
          }
        }
      } else if (
        formData.other_urls && typeof formData.other_urls === "object"
      ) {
        otherUrlsObj = formData.other_urls;
      }

      // Coerce possible objects/strings into primary key numbers
      const toPk = (v) => {
        if (v == null) return v;
        if (typeof v === 'object') {
          const cand = v.id ?? v.pk ?? v.value;
          if (cand != null) return Number(cand);
          return undefined;
        }
        const num = Number(v);
        return Number.isNaN(num) ? undefined : num;
      };

      const integrationPk = toPk(formData.platform);
      const organizationPk = toPk(user?.organization_id ?? user?.organization);

      const payload = {
        base_url: formData.base_url,
        secret_key: formData.secret_key,
        client_id: formData.client_id,
        // other_urls validado arriba
        other_urls: otherUrlsObj,
        is_active: Boolean(formData.is_active),
        integration: integrationPk,
        organization: organizationPk,
      };
      await customFetch.post(
        `/api/integrations/organization_platform/`,
        payload
      );
      
      // Refrescar la lista desde el backend
      const response = await customFetch.get(
        "/api/integrations/organization_platform/"
      );
      const integrationsData = response.data.results || response.data;
      setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
    } catch (error) {
      console.error("Error creating organization_platform:", error);
      throw error; // Para que el modal maneje el error
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      const url = `/api/integrations/organization_platform/${integrationId}/`;
      await customFetch.delete(url);
      setIntegrations((prev) => prev.filter((i) => i.id !== integrationId));
    } catch (error) {
      console.error("Error deleting organization_platform:", error);
    }
  };

  if (loading) return <div>{t('page.loading')}</div>;

  // Filtrar integraciones del backend según búsqueda y categoría
  const safeIntegrations = Array.isArray(integrations) ? integrations : [];
  const filteredIntegrations = safeIntegrations.filter((op) => {
    const q = (searchTerm || "").toLowerCase();
    if (!q) return true;
    const nested = op?.integration && typeof op.integration === "object" ? op.integration : {};
    const haystack = [
      op.display_name,
      op.name,
      op.integration_name,
      nested.display_name,
      nested.name,
      nested.integration_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  return (
    <div className="integrations-root">
      {/* Page Header */}
      <div className="integrations-header">
        <div>
          <h1 className="integrations-title">{t('page.title')}</h1>
          <p className="integrations-subtitle">
            {t('page.subtitle')}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="primary"
            onClick={() => {
              setIsModalOpen(true);
            }}
            leftIcon={<Plus size={16} />}
          >
            {t('page.buttonNew')}
          </Button>
        </div>
      </div>

      {/* All Integrations in One Frame */}
      <div className="integrations-integration-list-card">
        <div className="integrations-integration-list-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div>
              <h3 className="integrations-subtitle-h3 integrations-integration-list-title">
                {t('page.listTitle')}
              </h3>
              <p className="integrations-subtitle">
                {t('page.listSubtitle')}
              </p>
            </div>
          </div>
          <div className="integrations-searchbar">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('page.searchPlaceholder')}
              className="w-full max-w-md"
            />
          </div>
        </div>
        <div className="integrations-list">
          {filteredIntegrations.map((integration) => (
            <React.Fragment key={integration.id || integration.name}>
              <div
                className={`integrations-integration-card`}
                style={{ position: "relative" }}
              >
                <div
                  className="integrations-integration-card-content"
                  onClick={() => handleConfigureIntegration(integration)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="integrations-integration-icon">
                    <GraduationCap size={32} />
                  </div>
                  <div>
                    <div className="integrations-integration-title">
                      {(
                        (integration.integration && typeof integration.integration === 'object' && (integration.integration.display_name || integration.integration.name)) ||
                        integration.display_name || integration.name || integration.integration_name || t('page.untitled')
                      )}
                    </div>
                    {/* <div className="integrations-integration-type">
                      {(
                        (integration.integration && typeof integration.integration === 'object' && (integration.integration.name || integration.integration.integration_name)) ||
                        integration.integration_name || integration.name || t('page.notSpecified')
                      )}
                    </div> */}
                  </div>
                  <div className="integrations-integration-desc">
                    {t('page.lastModified')}{" "}
                    {integration.modified ? formatDate(integration.modified) : "-"}
                  </div>
                </div>
                <div className="integrations-integration-actions">
                  <Button
                    variant="primary"
                    size="lm"
                    onClick={() => handleConfigureIntegration(integration)}
                  >
                    {t('page.configure')}
                  </Button>
                </div>
              </div>
            </React.Fragment>
          ))}
          {/* Empty State */}
          {filteredIntegrations.length === 0 && (
            <div className="integrations-empty-state">
              <Database size={48} className="integrations-empty-state-icon" />
              <div className="integrations-empty-state-title">
                {t('page.emptyTitle')}
              </div>
              <div className="integrations-empty-state-desc">
                {searchTerm
                  ? t('page.emptyDescWithQuery')
                  : t('page.emptyDescNoQuery')}
              </div>
            </div>
          )}
        </div>

        {/* Configure Integration Modal */}
        <IntegrationModal
          isOpen={showConfigureModal}
          onClose={() => {
            setShowConfigureModal(false);
            setIntegrationToConfigure(null);
            setConfigData(null);
          }}
          integration={integrationToConfigure}
          configData={configData}
          onSave={handleSaveConfigIntegration}
          isSaving={isSavingConfig}
          mode="configure"
          onDelete={async (integration) => {
            if (!integration) return;
            try {
              setIsDeletingIntegration(true);
              await handleDeleteIntegration(integration.id);
              setShowConfigureModal(false);
              setIntegrationToConfigure(null);
              setConfigData(null);
            } finally {
              setIsDeletingIntegration(false);
            }
          }}
          isDeleting={isDeletingIntegration}
        />
      </div>

      {/* Add Integration Modal */}
      <IntegrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        integration={null}
        onSave={async (data, onError) => {
          try {
            await handleCreateOrganizationPlatform(data);
            setIsModalOpen(false);
          } catch (error) {
            if (error.response && error.response.data && onError) {
              onError(error.response.data);
            } else if (onError) {
              onError({ general: ["An error occurred. Please try again."] });
            }
            throw error; // No cerrar modal si hay error
          }
        }}
        existingIntegrations={integrations}
        mode="add"
      />
    </div>
  );
}
